import { Box, Flex, Stack, useMantineTheme } from '@mantine/core';
import classNames from 'classnames';
import type { IObservableArray } from 'mobx';
import { when } from 'mobx';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroupBase, OptionProps } from 'react-select-modern';

import type { DisplayCreative } from '@feathr/blackbox';
import {
  Button,
  CreativeListItem,
  Heading,
  Modal,
  UniversalSelect,
  zIndex,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import { useDuplicateCreative } from '../Creatives.hooks';
import type { ICreativeListItemData } from '../Creatives.utils';
import {
  formatCreativeInfo,
  getCreativeClassesForChannel,
  hasValidDimensions,
  isVideoCreative,
} from '../Creatives.utils';
import type { TChannel } from '../types';
import { getModalSubtitle, getModalTitle } from './DuplicateCreativeModal.utils';

import * as styles from './DuplicateCreativeModal.css';

export interface IDuplicateCreativeModalProps {
  /** Campaign ID to associate cloned creatives with */
  campaignId: string;
  /** Type of creative to duplicate */
  channel: TChannel;
  /** Observable array of creatives for optimistic updates */
  creatives?: IObservableArray<DisplayCreative>;
  /** Event ID for the cloned creative */
  eventId?: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to trigger appear animation after adding to list */
  onAppear?: (id: string) => void;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback when user clicks "Back" */
  onBack: () => void;
}

interface ICreativeSelectOption extends ICreativeListItemData {
  id: string;
  name: string;
  isVideo: boolean;
}

function CreativeOption<
  T extends ICreativeSelectOption,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<T> = GroupBase<T>,
>(props: OptionProps<T, IsMulti, Group>): JSX.Element {
  const { data, innerProps, isFocused, isSelected } = props;

  return (
    <div
      {...innerProps}
      className={classNames('react-select__option', styles.dropdownItem, {
        'react-select__option--is-focused': isFocused,
        'react-select__option--is-selected': isSelected,
        [styles.dropdownItemSelected]: isSelected,
      })}
    >
      <CreativeListItem
        aspectRatio={data.aspectRatio}
        dimensions={data.dimensions}
        isVideo={data.isVideo}
        name={`creative-option-${data.id}`}
        selected={isSelected}
        specName={data.specName}
        thumbnailUrl={data.thumbnailUrl}
        title={data.title}
      />
    </div>
  );
}

function DuplicateCreativeModal({
  campaignId,
  channel,
  creatives,
  eventId,
  isOpen,
  onAppear,
  onClose,
  onBack,
}: Readonly<IDuplicateCreativeModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { Creatives } = useStore();
  const [selectedCreatives, setSelectedCreatives] = React.useState<ICreativeSelectOption[]>([]);

  const { isDuplicating, duplicateCreatives } = useDuplicateCreative({
    campaignId,
    eventId,
    creatives,
    onAppear,
    onSuccess: onClose,
  });

  const PAGE_SIZE = 25;

  const loadPagedOptions = useCallback(
    async (
      inputValue: string,
      _prevOptions: readonly ICreativeSelectOption[],
      additional?: { page: number },
    ): Promise<{
      options: readonly ICreativeSelectOption[];
      hasMore: boolean;
      additional: { page: number };
    }> => {
      const page = additional?.page ?? 0;

      const response = Creatives.list({
        filters: {
          is_facebook: channel === 'meta',
          _cls__in: getCreativeClassesForChannel(channel),
          ...(inputValue ? { name__icontains: inputValue } : {}),
        },
        pagination: {
          page,
          page_size: PAGE_SIZE,
        },
        ordering: ['-date_created'],
      });

      await when(() => !response.isPending);

      const allModels = response.models as DisplayCreative[];
      const hasMore = allModels.length === PAGE_SIZE;

      const options = allModels.filter(hasValidDimensions).map((creative) => {
        const info = formatCreativeInfo(creative);
        return {
          id: creative.id,
          name: creative.get('name') ?? '',
          thumbnailUrl: creative.get('url') ?? '',
          title: creative.get('name') ?? '',
          specName: info.specName,
          dimensions: info.dimensions,
          aspectRatio: info.aspectRatio,
          isVideo: isVideoCreative(creative),
        };
      });

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    },
    [Creatives, channel],
  );

  function handleSelectCreatives(values: ICreativeSelectOption[]): void {
    setSelectedCreatives(values);
  }

  function handleRemoveCreative(creativeId: string): void {
    setSelectedCreatives((prev) => prev.filter((c) => c.id !== creativeId));
  }

  async function handleDuplicate(): Promise<void> {
    if (selectedCreatives.length === 0) {
      return;
    }
    await duplicateCreatives(selectedCreatives.map((c) => ({ id: c.id, name: c.name })));
  }

  const modalTitle = getModalTitle(channel, t);
  const modalSubtitle = getModalSubtitle(t);

  const leftActions = (
    <Button key={'back'} name={'back'} onClick={onBack}>
      {t('Back')}
    </Button>
  );

  const rightActions = [
    <Button key={'cancel'} name={'cancel'} onClick={onClose}>
      {t('Cancel')}
    </Button>,
    <Button
      disabled={selectedCreatives.length === 0 || isDuplicating}
      key={'duplicate'}
      name={'duplicate'}
      onClick={handleDuplicate}
      type={'primary'}
    >
      {isDuplicating ? t('Duplicating...') : t('Duplicate')}
    </Button>,
  ];

  return (
    <Modal
      description={modalSubtitle}
      leftActions={leftActions}
      onClose={onClose}
      opened={isOpen}
      rightActions={rightActions}
      title={modalTitle}
    >
      <Stack className={styles.container} gap={'0'}>
        <UniversalSelect<ICreativeSelectOption>
          cacheOptions={true}
          closeMenuOnScroll={false}
          components={{ Option: CreativeOption }}
          controlPrefix={getIconForAction('search')}
          controlShouldRenderValue={false}
          defaultAdditional={{ page: 0 }}
          defaultOptions={true}
          hideSelectedOptions={true}
          isClearable={false}
          isMulti={true}
          isSearchable={true}
          loadPagedOptions={loadPagedOptions}
          menuPortalTarget={document.body}
          menuPosition={'fixed'}
          name={'creative-select'}
          onChange={handleSelectCreatives}
          paginate={true}
          placeholder={t('Search by name')}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: zIndex.popover }) }}
          value={selectedCreatives}
        />

        {selectedCreatives.length > 0 && (
          <Stack className={styles.selectedSection} gap={theme.spacing['3']}>
            <Heading order={6} variant={'h6'}>
              {t('Selected Creatives ({{count}})', { count: selectedCreatives.length })}
            </Heading>
            <Stack gap={theme.spacing['1']}>
              {selectedCreatives.map((creative) => (
                <Flex align={'center'} className={styles.selectedItem} key={creative.id}>
                  <Box className={styles.selectedItemContent}>
                    <CreativeListItem
                      aspectRatio={creative.aspectRatio}
                      dimensions={creative.dimensions}
                      isVideo={creative.isVideo}
                      name={`selected-creative-${creative.id}`}
                      specName={creative.specName}
                      thumbnailUrl={creative.thumbnailUrl}
                      title={creative.title}
                    />
                  </Box>
                  <button
                    aria-label={t('Remove {{name}}', { name: creative.title })}
                    className={styles.removeButton}
                    onClick={(): void => handleRemoveCreative(creative.id)}
                    type={'button'}
                  >
                    {getIconForAction('close')}
                  </button>
                </Flex>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}

export default DuplicateCreativeModal;
